/**
 * 📊 Merkezi Realtime Servisi — Structured Logger
 * Tüm Realtime olaylarını tutarlı formatta loglar.
 */

type LogLevel = 'info' | 'warn' | 'error' | 'debug'

interface LogEntry {
  level: LogLevel
  source: string
  event: string
  data?: Record<string, unknown>
  timestamp: string
}

const LOG_PREFIX = '📡 [Realtime]'

const LEVEL_ICONS: Record<LogLevel, string> = {
  info: 'ℹ️',
  warn: '⚠️',
  error: '❌',
  debug: '🔍',
}

class RealtimeLogger {
  private enabled = true

  enable() { this.enabled = true }
  disable() { this.enabled = false }

  private log(level: LogLevel, source: string, event: string, data?: Record<string, unknown>) {
    if (!this.enabled) return

    const entry: LogEntry = {
      level,
      source,
      event,
      data,
      timestamp: new Date().toISOString(),
    }

    const icon = LEVEL_ICONS[level]
    const prefix = `${LOG_PREFIX} ${icon} [${source}]`
    const message = `${prefix} ${event}`

    if (level === 'error') {
      console.error(message, data ?? '')
    } else if (level === 'warn') {
      console.warn(message, data ?? '')
    } else if (level === 'debug') {
      console.debug(message, data ?? '')
    } else {
      console.log(message, data ?? '')
    }

    return entry
  }

  // ─── Connection Events ───

  connecting(groupId: string) {
    this.log('info', 'Connection', 'Bağlantı kuruluyor', { groupId })
  }

  connected(groupId: string, channelCount: number) {
    this.log('info', 'Connection', 'Tüm kanallar aktif', { groupId, channelCount })
  }

  reconnecting(attempt: number, delayMs: number) {
    this.log('warn', 'Connection', `Yeniden bağlanma denemesi #${attempt}`, { delayMs })
  }

  reconnected(groupId: string) {
    this.log('info', 'Connection', 'Yeniden bağlantı başarılı', { groupId })
  }

  degraded(groupId: string) {
    this.log('warn', 'Connection', 'DEGRADED moda geçildi — polling aktif', { groupId })
  }

  failed(groupId: string, reason: string) {
    this.log('error', 'Connection', 'Bağlantı başarısız', { groupId, reason })
  }

  disconnected(groupId: string) {
    this.log('info', 'Connection', 'Temiz ayrılma', { groupId })
  }

  // ─── Channel Events ───

  channelSubscribed(channelName: string) {
    this.log('debug', 'Channel', `${channelName} aktif`)
  }

  channelError(channelName: string, error?: string) {
    this.log('error', 'Channel', `${channelName} hata`, { error })
  }

  channelTimeout(channelName: string) {
    this.log('warn', 'Channel', `${channelName} zaman aşımı`)
  }

  // ─── GroupSync Events ───

  groupUpdated(field: string, value: unknown) {
    this.log('info', 'GroupSync', `Grup güncellendi: ${field}`, { [field]: value })
  }

  // ─── Draw Events ───

  drawStarting(groupId: string) {
    this.log('info', 'DrawLive', 'Çekiliş başlıyor', { groupId })
  }

  turnRevealed(turnIndex: number, winnerId: string) {
    this.log('info', 'DrawLive', `Tur #${turnIndex} açıklandı`, { winnerId })
  }

  drawComplete(groupId: string, totalTurns: number) {
    this.log('info', 'DrawLive', 'Çekiliş tamamlandı', { groupId, totalTurns })
  }

  // ─── Presence Events ───

  presenceSync(spectatorCount: number) {
    this.log('debug', 'Presence', `${spectatorCount} izleyici`, { spectatorCount })
  }

  presenceJoin(userId: string) {
    this.log('debug', 'Presence', 'Kullanıcı katıldı', { userId })
  }

  presenceLeave(userId: string) {
    this.log('debug', 'Presence', 'Kullanıcı ayrıldı', { userId })
  }

  // ─── Polling Events ───

  pollingStarted(reason: string) {
    this.log('info', 'Polling', `Polling başlatıldı: ${reason}`)
  }

  pollingStopped() {
    this.log('debug', 'Polling', 'Polling durduruldu')
  }

  pollingResult(found: boolean, data?: Record<string, unknown>) {
    this.log('debug', 'Polling', found ? 'Veri bulundu' : 'Henüz veri yok', data)
  }

  // ─── Notification Sync ───

  toastSuppressed(notificationType: string) {
    this.log('info', 'NotifSync', `Toast bastırıldı (ceremonyActive)`, { notificationType })
  }

  toastReleased(count: number) {
    this.log('info', 'NotifSync', `Bastırılan ${count} toast serbest bırakıldı`)
  }

  // ─── Heartbeat ───

  heartbeat(channelStates: Record<string, string>) {
    this.log('debug', 'Heartbeat', 'Kanal durumları', channelStates)
  }
}

export const realtimeLogger = new RealtimeLogger()
